import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EntryErrors } from "./EntryErrors.js";
import {
  addItem,
  convertToEntry,
  createEntryValues,
  createInitialErrors,
  validateEntry
} from "./EntryValues.js";
import { ItemInputs } from "./ItemInputs.js";
import { ChangesTable } from "./ChangesTable.js";
import { LinkMap, createLinkMap } from "./LinkMap.js";
import { EntryValues } from "../../../model/accounting/form/EntryValues.js";
import { useForm } from "../../../../packages/form/useForm.js";
import { createUpdateProperty } from "../../../../packages/optics/optics/property/createUpdateProperty.js";
import { createOperate } from "../../../../packages/optics/optics/self/createOperate.js";
import { createWriteProperty } from "../../../../packages/optics/optics/property/createWriteProperty.js";
import { createRemoveIndex } from "../../../../packages/optics/optics/index/createRemoveIndex.js";
import { createMoveIndexUp } from "../../../../packages/optics/optics/index/createMoveIndexUp.js";
import { createMoveIndexDown } from "../../../../packages/optics/optics/index/createMoveIndexDown.js";
import { useHref } from "../../../../packages/router/href/HrefProvider.js";
import { Form } from "../../../../packages/ui/form/Form.js";
import { FormRow } from "../../../../packages/ui/form/FormRow.js";
import { Input } from "../../../../packages/ui/form/Input.js";
import { Alert } from "../../../../packages/ui/Alert.js";
import { Buttons } from "../../../../packages/ui/form/Buttons.js";
import { Button } from "../../../../packages/ui/button/Button.js";
import { saveEntry, updateEntry } from "../accountingApi.js";
import { EntryAction } from "./EntryAction.js";

type Props = {
  action: EntryAction;
};

/**
 * Renders form to create new entries or update existing ones.
 */
export const EntryForm = ({ action }: Props) => {
  const { setHref } = useHref();

  const submit = useCallback(
    async (values: EntryValues) => {
      const newEntry = convertToEntry(values);
      if (action.type === "new" || action.type === "copy") {
        const entryId = await saveEntry(newEntry);
        setHref(`/entry/${entryId}`);
      } else if (action.type === "update") {
        await updateEntry(action.entry.id, newEntry);
        setHref(`/entry/${action.entry.id}`);
      }
    },
    [action, setHref]
  );

  const handleSubmissionError = useCallback((err: unknown) => {
    return { generic: err + "", items: [] };
  }, []);

  const [linkMap, setLinkMap] = useState<LinkMap>({ dates: {}, amounts: {} });

  const entry = action.type === "copy" || action.type === "update" ? action.entry : undefined;

  const initialValues = useMemo(() => createEntryValues(entry), [entry]);
  const initialErrors = useMemo(() => createInitialErrors(entry), [entry]);

  useEffect(() => {
    setLinkMap(createLinkMap(initialValues));
  }, [initialValues]);

  const { values, setValues, handleSubmit, errors } = useForm<EntryValues, EntryErrors>({
    initialValues,
    initialErrors,
    validate: validateEntry,
    submit,
    handleSubmissionError
  });

  const handleItemsChange = createUpdateProperty(setValues, "items");
  const handleAddItem = createOperate(handleItemsChange, addItem);

  const displayedEntryId = action.type === "update" ? action.entry.id : "";
  const saveLabel =
    action.type === "copy" ? "Salvesta koopia" : action.type === "update" ? "Salvesta" : "Salvesta uus";

  return (
    <>
      <h2>{values.title}</h2>
      <div style={{ minHeight: "200px" }}>
        <ChangesTable entry={values} />
      </div>
      <Form onSubmit={handleSubmit}>
        <FormRow columns={2}>
          <Input
            label="Pealkiri"
            error={errors.title}
            value={values.title}
            placeholder="Pealkiri"
            onChange={createWriteProperty(setValues, "title")}
          />
          <Input label="ID" value={displayedEntryId} readOnly={true} disabled={true} />
        </FormRow>
        {values.items.map((_, index) => (
          <ItemInputs
            key={index}
            index={index}
            numOfItems={values.items.length}
            errors={errors.items[index] ?? {}}
            values={values.items[index]}
            onRemove={createRemoveIndex(handleItemsChange, index)}
            onUpdateItems={handleItemsChange}
            onUp={createMoveIndexUp(handleItemsChange, index)}
            onDown={createMoveIndexDown(handleItemsChange, index)}
            linkMap={linkMap}
            onUpdateLinkMap={setLinkMap}
          />
        ))}
        {errors.generic && (
          <FormRow columns={1}>
            <Alert type="error">{errors.generic}</Alert>
          </FormRow>
        )}
        <FormRow columns={1}>
          <Buttons>
            <Button onClick={handleAddItem}>Lisa kirje</Button>
            <Button type="submit">{saveLabel}</Button>
          </Buttons>
        </FormRow>
      </Form>
    </>
  );
};
